// frontend/src/hooks/useConnectorMutations.js
//
// Write-side hooks for the Connectors page (the read list lives in
// useConnectors). Two operations drive the per-merchant Exact connect flow:
//
//   useCreateExactConnector — POST the merchant's own Exact app credentials
//                             (ClientID / Client Secret / Webhook Secret).
//                             Secrets are write-only; they never come back.
//   useExactAuthorizeUrl    — fetch the OAuth URL for a connector, then the
//                             caller redirects the browser to Exact.
//
// Every write names the connectors as changed and, where a Resolve step
// writes store data (suppliers, items, locations, purchase orders), that
// data too (`queries/dataChanged`). All route through apiJson so CSRF,
// store-scoping, and structured-error handling stay centralized.

import { useMutation, useQuery } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { connectors as connectorsKey } from '../queries/keys';
import { useApiMutation } from '../queries/useApiMutation';

export function useCreateExactConnector() {
  return useApiMutation({
    mutationFn: (payload) => apiJson.post('/api/connectors/exact/', payload),
    changes: [DATA.connectors],
  });
}

export function useExactAuthorizeUrl() {
  return useMutation({
    mutationFn: (connectorId) =>
      apiJson.get(`/api/connectors/${connectorId}/exact/authorize-url/`),
  });
}

// Start the initial pull + stage + reconcile for an authorized connector →
// advances it to RESOLVE so the review wizards appear. Deliberate
// merchant-triggered action (staging the whole catalog is too slow to run in
// the OAuth callback).
export function useExactStartSync() {
  return useApiMutation({
    mutationFn: (connectorId) =>
      apiJson.post(`/api/connectors/${connectorId}/exact/start-sync/`),
    changes: [DATA.connectors],
  });
}

// The read-only activity detail for one connector — health, last sync,
// current counts, pending reviews, skipped-with-reason, and recent webhook
// deliveries. Powers the "Details" drawer; fetched only while it's open.
export function useConnectorActivity(connectorId, enabled) {
  const { selectedStore } = useAuth();
  return useQuery({
    queryKey: connectorsKey.activity(selectedStore?.id, connectorId),
    queryFn: () => apiJson.get(`/api/connectors/${connectorId}/activity/`),
    enabled: !!connectorId && !!enabled,
    staleTime: 0,
  });
}

// The supplier-selection rows for the Resolve wizard — read-only, fetched
// when a connector is parked at RESOLVE awaiting merchant confirmation.
// 409 when the connector isn't in that phase (the caller hides the wizard).
export function useExactSupplierSelection(connectorId, enabled) {
  const { selectedStore } = useAuth();
  return useQuery({
    queryKey: connectorsKey.supplierSelection(selectedStore?.id, connectorId),
    queryFn: () =>
      apiJson.get(`/api/connectors/${connectorId}/exact/supplier-selection/`),
    enabled: !!connectorId && !!enabled,
    staleTime: 0,
  });
}

// Commit the merchant's chosen suppliers → clears the supplier gate (and
// activates the connector once every blocking gate is cleared).
export function useExactConfirmSuppliers(connectorId) {
  return useApiMutation({
    mutationFn: ({ selectedIds, pairings }) =>
      apiJson.post(`/api/connectors/${connectorId}/exact/confirm-suppliers/`, {
        selected_ids: selectedIds,
        pairings: pairings || {},
      }),
    changes: [DATA.connectors, DATA.suppliers],
  });
}

// Candidates to link a Manexys-native supplier (no erp_id) to its Exact
// account — local linked suppliers to MERGE into + live Exact accounts to
// ADOPT. Active connectors only; re-fetched as the merchant types (the query
// is part of the key). See exact_app.services.supplier_link.
export function useExactSupplierLinkCandidates(connectorId, supplierId, query, enabled) {
  const { selectedStore } = useAuth();
  return useQuery({
    queryKey: [
      'connectors', selectedStore?.id, 'supplier-link-candidates',
      connectorId, supplierId, query,
    ],
    queryFn: () =>
      apiJson.get(
        `/api/connectors/${connectorId}/exact/supplier-link-candidates/`
        + `?supplier_id=${supplierId}&q=${encodeURIComponent(query || '')}`,
      ),
    enabled: !!connectorId && !!supplierId && !!enabled,
    staleTime: 0,
  });
}

// Link a native supplier to a chosen Exact account (adopt, merging an
// auto-imported duplicate into it when one exists). The supplier now carries
// an erp_id, which unblocks its recommendations and clears its Data Health
// issue.
export function useExactLinkSupplier(connectorId) {
  return useApiMutation({
    mutationFn: ({ nativeSupplierId, exactAccountId }) =>
      apiJson.post(`/api/connectors/${connectorId}/exact/link-supplier/`, {
        native_supplier_id: nativeSupplierId,
        exact_account_id: exactAccountId,
      }),
    changes: [DATA.connectors, DATA.suppliers],
  });
}

// The item-match rows for the Resolve wizard — staged Exact items matched to
// existing Manexys items by the backend's multi-signal cascade (or the
// merchant's nominated key). Computed live server-side on every read.
// Same RESOLVE-gated read shape as the supplier selection.
export function useExactItemSelection(connectorId, enabled) {
  const { selectedStore } = useAuth();
  return useQuery({
    queryKey: connectorsKey.itemSelection(selectedStore?.id, connectorId),
    queryFn: () =>
      apiJson.get(`/api/connectors/${connectorId}/exact/item-selection/`),
    enabled: !!connectorId && !!enabled,
    staleTime: 0,
  });
}

// Apply the merchant's per-row item decisions → clears the item gate (and
// activates the connector once every blocking gate is cleared). ``actions``
// maps Exact item id → { action: 'link'|'import'|'skip', item_id? }.
export function useExactConfirmItems(connectorId) {
  return useApiMutation({
    mutationFn: (actions) =>
      apiJson.post(`/api/connectors/${connectorId}/exact/confirm-items/`, {
        actions: actions || {},
      }),
    changes: [DATA.connectors, DATA.items],
  });
}

// The warehouse-match rows for the Resolve wizard — staged Exact warehouses
// matched to existing Manexys Locations by the backend's Code → Name cascade.
// Same RESOLVE-gated read shape as the supplier / item selections.
export function useExactLocationSelection(connectorId, enabled) {
  const { selectedStore } = useAuth();
  return useQuery({
    queryKey: connectorsKey.locationSelection(selectedStore?.id, connectorId),
    queryFn: () =>
      apiJson.get(`/api/connectors/${connectorId}/exact/location-selection/`),
    enabled: !!connectorId && !!enabled,
    staleTime: 0,
  });
}

// Apply the merchant's per-row warehouse decisions → clears the location gate
// (and activates the connector once every blocking gate is cleared).
// ``actions`` maps Exact warehouse id → { action: 'link'|'import'|'skip',
// location_id? }.
export function useExactConfirmLocations(connectorId) {
  return useApiMutation({
    mutationFn: (actions) =>
      apiJson.post(`/api/connectors/${connectorId}/exact/confirm-locations/`, {
        actions: actions || {},
      }),
    changes: [DATA.connectors, DATA.locations],
  });
}

// The PO-match rows for the (deferred) Resolve gate — each non-terminal legacy
// Manexys PO matched against the live Exact mirrors by the backend's composite
// supplier+line-set key. RESOLVE-gated read, same shape as the other selections.
export function useExactPoSelection(connectorId, enabled) {
  const { selectedStore } = useAuth();
  return useQuery({
    queryKey: connectorsKey.poSelection(selectedStore?.id, connectorId),
    queryFn: () =>
      apiJson.get(`/api/connectors/${connectorId}/exact/po-selection/`),
    enabled: !!connectorId && !!enabled,
    staleTime: 0,
  });
}

// Apply the merchant's per-PO dispositions → clears the po_match gate (and
// activates the connector, it being the last/deferred gate). ``actions`` maps
// PO id → { action: 'link'|'push'|'keep'|'discard', mirror_id? }.
export function useExactConfirmPos(connectorId) {
  return useApiMutation({
    mutationFn: (actions) =>
      apiJson.post(`/api/connectors/${connectorId}/exact/confirm-pos/`, {
        actions: actions || {},
      }),
    changes: [DATA.connectors, DATA.purchaseOrders],
  });
}

// What disconnecting WOULD do — live counts, no mutation. Drives the
// confirmation dialog: items/locations to delete, survivors to unlink, and
// rows kept-but-flagged because they anchor history. 409 if not ACTIVE.
export function useExactDisconnectPreview(connectorId, enabled) {
  const { selectedStore } = useAuth();
  return useQuery({
    queryKey: connectorsKey.disconnectPreview(selectedStore?.id, connectorId),
    queryFn: () =>
      apiJson.get(`/api/connectors/${connectorId}/exact/disconnect-preview/`),
    enabled: !!connectorId && !!enabled,
    staleTime: 0,
  });
}

// Disconnect the connector — purge ERP-only data, strip ERP identity from
// survivors, deauthorize, flip to DEACTIVATED. Destructive + irreversible;
// the caller gates it behind an explicit confirmation.
export function useDisconnectExact(connectorId) {
  return useApiMutation({
    mutationFn: () =>
      apiJson.post(`/api/connectors/${connectorId}/exact/disconnect/`),
    changes: [
      DATA.connectors, DATA.items, DATA.inventory, DATA.locations, DATA.suppliers,
      DATA.supplierLinks, DATA.purchaseOrders,
    ],
  });
}
